import React, { lazy, Suspense, useCallback, useState } from "react";
import PageSkeleton from "@/components/PageSkeleton";
import PhoneRequiredModal from "@/components/auth/PhoneRequiredModal";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BatchProvider } from "@/context/BatchContext";
import { StoreProvider } from "@/context/StoreContext";
import OfflineIndicator from "@/components/OfflineIndicator";
import useLenis from "@/hooks/useLenis";
import useVoucherFromUrl from "@/hooks/useVoucherFromUrl";
import Header from "@/components/Header";
import CartDrawer from "@/components/CartDrawer";
import AuthModal from "@/components/AuthModal";
import BirthdayPrompt from "@/components/auth/BirthdayPrompt";
import { useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
const Orders = lazy(() => import("@/pages/Orders"));
const OrderTracking = lazy(() => import("@/pages/OrderTracking"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const Live = lazy(() => import("@/pages/admin/Live"));
const Money = lazy(() => import("@/pages/admin/Money"));
const Customers = lazy(() => import("@/pages/admin/Customers"));
const Setup = lazy(() => import("@/pages/admin/Setup"));
const MediaPage = lazy(() => import("@/pages/admin/MediaPage"));
const KitchenDashboard = lazy(() => import("@/pages/KitchenDashboard"));
const Accounting = lazy(() => import("@/pages/admin/Accounting"));
const MenuManager = lazy(() => import("@/pages/admin/MenuManager"));
const StaffHR = lazy(() => import("@/pages/admin/StaffHR"));
const Marketing = lazy(() => import("@/pages/admin/Marketing"));
const QRCodePage = lazy(() => import("@/pages/admin/QRCode"));
const Batches = lazy(() => import("@/pages/admin/Batches"));
const Vouchers = lazy(() => import("@/pages/admin/Vouchers"));
const WhatsAppAdmin = lazy(() => import("@/pages/admin/WhatsApp"));
const WalkinOrder = lazy(() => import("@/pages/admin/WalkinOrder"));
const Reconciliation = lazy(() => import("@/pages/admin/Reconciliation"));
const Settlement = lazy(() => import("@/pages/admin/Settlement"));
const OrdersAdmin = lazy(() => import("@/pages/admin/OrdersAdmin"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Nutrition = lazy(() => import("@/pages/Nutrition"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const GiftCards = lazy(() => import("@/pages/GiftCards"));
const Account = lazy(() => import("@/pages/Account"));
const ApiDocs = lazy(() => import("@/pages/ApiDocs"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const MenuRedirect = lazy(() => import("@/pages/MenuRedirect"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const OrderConfirmed = lazy(() => import("@/pages/OrderConfirmed"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import MobileCartBar from "@/components/MobileCartBar";
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
import { LEGAL_ROUTES } from "@/pages/legalRoutes";
const LegalPage = lazy(() => import("@/pages/LegalPage"));

function Shell() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = useCallback(() => setAuthOpen(true), []);
  const handleAuthOpenChange = useCallback((nextOpen) => setAuthOpen(Boolean(nextOpen)), []);
  const { pathname } = useLocation();
  const staffArea = pathname.startsWith("/admin") || pathname.startsWith("/kitchen");
  useLenis(!staffArea);
  useVoucherFromUrl();

  return (
    <>
      <OfflineIndicator />
      <Header onAuthClick={openAuth} />
      <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/track/:orderNumber" element={<OrderTracking />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Live />} />
          <Route path="customers" element={<Customers />} />
          <Route path="money" element={<Money />} />
          <Route path="setup" element={<Setup />} />
          <Route path="media" element={<MediaPage />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="menu" element={<MenuManager />} />
          <Route path="staff" element={<StaffHR />} />
          <Route path="marketing" element={<Marketing />} />
          <Route path="qr" element={<QRCodePage />} />
          <Route path="batches" element={<Batches />} />
          <Route path="vouchers" element={<Vouchers />} />
          <Route path="whatsapp" element={<WhatsAppAdmin />} />
          <Route path="walkin" element={<WalkinOrder />} />
          <Route path="reconciliation" element={<Reconciliation />} />
          <Route path="settlement" element={<Settlement />} />
          <Route path="orders" element={<OrdersAdmin />} />
        </Route>
        <Route path="/kitchen" element={<KitchenDashboard />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/nutrition" element={<Nutrition />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/account" element={<Account />} />
        <Route path="/agent-docs" element={<ApiDocs />} />
        <Route path="/menu" element={<MenuRedirect />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/order-confirmed/:orderNumber" element={<OrderConfirmed />} />
        <Route path="/pizza/:slug" element={<ProductDetail />} />
        {LEGAL_ROUTES.map((r) => (
          <Route key={r.path} path={r.path} element={<LegalPage slug={r.slug} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {!staffArea && !authOpen && !["/reset-password", "/auth/callback"].some((r) => pathname.startsWith(r)) && <MobileCartBar user={user} onAuthClick={openAuth} />}
      <CartDrawer onAuthClick={openAuth} />
      <AuthModal open={authOpen} onOpenChange={handleAuthOpenChange} />
      <BirthdayPrompt />
      {!staffArea && <Suspense fallback={null}><ChatWidget /></Suspense>}
      {!staffArea && <PhoneRequiredModal />}
      <Toaster
        position={staffArea ? "top-right" : "bottom-center"}
        offset={staffArea ? 88 : undefined}
        mobileOffset={staffArea ? undefined : { bottom: 136 }}
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#FFFDF8",
            border: "2px solid #FF5003",
            borderRadius: "9999px",
            fontWeight: 600,
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <StoreProvider>
            <BatchProvider>
              <CartProvider>
                <Shell />
              </CartProvider>
            </BatchProvider>
          </StoreProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
